/**
 * Elite Batch 8 Part 4 — Workflow Templates 718–732
 * HR chatbots, recruiting, onboarding, leave management, and PTO automation workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 8 PART 4 — TEMPLATES 718–732
// =============================================================================

export const ELITE_BATCH8_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 718. HR POLICY Q&A BOT (7 nodes)
  // ===================================================================

  "HR Policy Q&A Bot": {
    summary:
      "AI-powered chatbot that answers employee HR policy questions using RAG vector search. A chat trigger extracts the question and employee context via AI, Pinecone searches policy documents, AI generates a jurisdictionally aware answer, a sensitive topic condition routes escalations to HR via Slack and Zendesk ticket creation, and all topics are anonymized and logged to Sheets for trend analysis.",
    nodes: [
      trigger("Chat trigger: HR policy question", "webhook_form", {
        source: "Internal Portal / Slack",
        event: "hr.policy.question",
        howItStarts:
          "An employee submits an HR policy question via internal chat portal, Slack bot, or intranet widget",
        setup:
          "Configure internal chat widget or Slack bot command to POST employee question payloads to this workflow URL. Include user identity token for jurisdiction detection.",
        inputFields: [],
      }),

      // 2. AI extraction + RAG policy search (Pinecone)
      n("2", "hitApi", "RAG: Search policy documents (Pinecone)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/embeddings",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "text-embedding-3-small" },
            { key: "input", value: "{webhook_payload}" },
          ],
          variables: [
            {
              name: "data[0].embedding",
              customName: "query_embedding",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI jurisdictional answer
      aiNode(
        "3",
        "AI: Generate jurisdictional policy answer",
        520,
        "You are an HR policy expert. Given an employee question and retrieved policy document chunks, produce an accurate, jurisdiction-aware answer. Identify the employee and their question from the raw payload. Cross-reference retrieved policy sections. Flag if the topic is sensitive (termination, harassment, discrimination, legal complaints, whistleblower, medical/disability accommodations). Respond JSON: {employee_email: string, employee_name: string, question: string, jurisdiction: string, answer: string, policy_sections_cited: string, is_sensitive: string, sensitive_category: string, confidence: number, topic_category: string}",
        "Employee question payload: {webhook_payload}. Retrieved policy context (embedding match): {query_embedding}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "question", as: "question", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          { field: "answer", as: "policy_answer", type: "string" },
          {
            field: "policy_sections_cited",
            as: "policy_sections_cited",
            type: "string",
          },
          { field: "is_sensitive", as: "is_sensitive", type: "string" },
          {
            field: "sensitive_category",
            as: "sensitive_category",
            type: "string",
          },
          { field: "confidence", as: "answer_confidence", type: "number" },
          { field: "topic_category", as: "topic_category", type: "string" },
        ],
      ),

      // 4. Sensitive topic condition
      n("4", "condition", "Sensitive topic requiring HR escalation?", 760, {
        conditionData: [
          {
            variable: "is_sensitive",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Immediate HR escalation (Slack) — True branch
      slackNode(
        "5",
        "Escalate to HR team (Slack)",
        1000,
        ":rotating_light: *Sensitive HR Policy Question*\\nEmployee: {employee_name} ({employee_email})\\nJurisdiction: {jurisdiction}\\nCategory: {sensitive_category}\\nQuestion: {question}\\nAI Answer (draft): {policy_answer}\\nSections cited: {policy_sections_cited}\\nPlease review and follow up directly.",
        -220,
      ),

      // 6. HR ticket creation (Zendesk) — also True branch
      n(
        "6",
        "hitApi",
        "Create HR ticket (Zendesk)",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Bearer {zendesk_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"subject":"Sensitive HR Policy Inquiry: {sensitive_category}","description":"Employee: {employee_name} ({employee_email})\\nJurisdiction: {jurisdiction}\\nQuestion: {question}\\nAI Draft Answer: {policy_answer}\\nPolicy Sections: {policy_sections_cited}","priority":"high","type":"task","tags":["hr-policy","sensitive","{sensitive_category}"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "zendesk_ticket_id",
                type: "number",
              },
              {
                name: "ticket.url",
                customName: "zendesk_ticket_url",
                type: "string",
              },
            ],
          },
        },
        -220,
      ),

      // 7. Anonymized topic logging (Sheets) — both branches converge
      sheetsNode(
        "7",
        "Log anonymized topic (Sheets)",
        1480,
        "HR_Policy_Topics",
        '[["{topic_category}","{jurisdiction}","{is_sensitive}","{sensitive_category}","{answer_confidence}","{policy_sections_cited}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // sensitive -> Slack escalation
      e("4", "7", "output_2"), // not sensitive -> log directly
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 719. INTERNAL JOB BOARD CHATBOT (8 nodes)
  // ===================================================================

  "Internal Job Board Chatbot": {
    summary:
      "Conversational chatbot that helps employees explore internal transfer opportunities. A chat trigger captures interest via AI, searches internal roles against employee skills, compares competencies via runCode, checks transfer eligibility via condition, presents matched roles, initiates the application flow, and notifies the internal mobility partner via Slack.",
    nodes: [
      trigger("Chat trigger: Internal job exploration", "webhook_form", {
        source: "Internal Portal / Slack",
        event: "internal.job.inquiry",
        howItStarts:
          "An employee initiates a conversation about internal job opportunities via the career portal chatbot or Slack command",
        setup:
          "Configure internal career portal chat widget or Slack slash command (/explore-roles) to POST to this workflow URL. Include employee ID or email for profile lookup.",
        inputFields: [],
      }),

      // 2. AI interest exploration
      aiNode(
        "2",
        "AI: Explore career interests",
        280,
        "You are an internal career advisor chatbot. Parse the incoming payload to identify the employee and understand their career interests, preferred departments, desired role level, and any specific skills they want to leverage. Extract their current role information if available. Respond JSON: {employee_email: string, employee_name: string, employee_id: string, current_role: string, current_department: string, interests: string, preferred_departments: string, desired_level: string, key_skills: string, years_in_current_role: number, search_query: string}",
        "Employee career inquiry payload: {webhook_payload}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "current_role", as: "current_role", type: "string" },
          {
            field: "current_department",
            as: "current_department",
            type: "string",
          },
          { field: "interests", as: "interests", type: "string" },
          {
            field: "preferred_departments",
            as: "preferred_departments",
            type: "string",
          },
          { field: "desired_level", as: "desired_level", type: "string" },
          { field: "key_skills", as: "key_skills", type: "string" },
          {
            field: "years_in_current_role",
            as: "years_in_role",
            type: "number",
          },
          { field: "search_query", as: "search_query", type: "string" },
        ],
      ),

      // 3. Internal role search
      n("3", "hitApi", "Search internal job postings", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://harvest.greenhouse.io/v1/jobs?status=open&per_page=50",
          headers: [
            {
              key: "Authorization",
              value: "Basic {greenhouse_api_key_base64}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "body", customName: "open_roles_json", type: "string" },
          ],
        },
      }),

      // 4. Skill comparison (runCode)
      n("4", "runCode", "Compare skills to open roles", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare employee skills against open internal roles
const roles = JSON.parse(vars.open_roles_json || "[]");
const skills = (vars.key_skills || "").toLowerCase().split(",").map(s => s.trim());
const prefs = (vars.preferred_departments || "").toLowerCase();
const level = (vars.desired_level || "").toLowerCase();
const yearsInRole = parseInt(vars.years_in_role) || 0;

const scored = roles.map(r => {
  const title = (r.name || r.title || "").toLowerCase();
  const dept = ((r.departments || [{}])[0].name || "").toLowerCase();
  let score = 0;
  skills.forEach(sk => { if (title.includes(sk) || (r.notes || "").toLowerCase().includes(sk)) score += 20; });
  if (prefs && prefs.includes(dept)) score += 25;
  if (level && title.includes(level)) score += 15;
  return { id: r.id, title: r.name || r.title, department: dept, score: score, url: r.absolute_url || "" };
}).filter(r => r.score > 0).sort((a, b) => b.score - a.score).slice(0, 5);

const eligible = yearsInRole >= 1 ? "yes" : "no";
const eligibilityReason = yearsInRole >= 1 ? "Meets minimum 1-year tenure requirement" : "Must complete " + (1 - yearsInRole) + " more year(s) in current role";
const topMatches = scored.map(r => r.title + " (" + r.department + ") - Match: " + r.score + "%").join("; ");
const matchCount = scored.length;

return {
  transfer_eligible: eligible,
  eligibility_reason: eligibilityReason,
  matched_roles: topMatches,
  match_count: matchCount,
  top_role_id: scored.length > 0 ? String(scored[0].id) : "",
  top_role_title: scored.length > 0 ? scored[0].title : "No matches found",
  matched_roles_json: JSON.stringify(scored)
};`,
          outputVariables: [
            { name: "transfer_eligible", type: "string" },
            { name: "eligibility_reason", type: "string" },
            { name: "matched_roles", type: "string" },
            { name: "match_count", type: "number" },
            { name: "top_role_id", type: "string" },
            { name: "top_role_title", type: "string" },
            { name: "matched_roles_json", type: "string" },
          ],
        },
      }),

      // 5. Transfer eligibility condition
      n("5", "condition", "Transfer eligibility met?", 1000, {
        conditionData: [
          {
            variable: "transfer_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Matched roles response (sendMessage) — True branch
      n("6", "sendMessage", "Present matched roles to employee", 1240, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "Great news, {employee_name}! Based on your skills ({key_skills}) and interests, I found {match_count} internal opportunities:\\n\\n{matched_roles}\\n\\nYour top match: {top_role_title}\\n\\nWould you like to start an application for any of these roles?",
        },
      }),

      // 7. Application flow initiation
      n("7", "hitApi", "Initiate internal application (Greenhouse)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/candidates",
          headers: [
            {
              key: "Authorization",
              value: "Basic {greenhouse_api_key_base64}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "first_name", value: "{employee_name}" },
            {
              key: "email_addresses",
              value: '[{"value":"{employee_email}","type":"work"}]',
            },
            { key: "applications", value: '[{"job_id":{top_role_id}}]' },
            { key: "tags", value: '["internal-transfer"]' },
          ],
          variables: [
            { name: "id", customName: "candidate_id", type: "number" },
            {
              name: "applications[0].id",
              customName: "application_id",
              type: "number",
            },
          ],
        },
      }),

      // 8. Mobility partner notification (Slack)
      slackNode(
        "8",
        "Notify mobility partner (Slack)",
        1720,
        ":arrows_counterclockwise: *Internal Transfer Interest*\\nEmployee: {employee_name} ({employee_email})\\nCurrent: {current_role} in {current_department} ({years_in_role}yr)\\nTarget: {top_role_title}\\nMatches found: {match_count}\\nEligibility: {eligibility_reason}\\nApplication ID: {application_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // eligible -> show matches
      e("5", "8", "output_2"), // not eligible -> notify mobility partner of ineligible inquiry
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 720. INTERNAL JOB POSTING (7 nodes)
  // ===================================================================

  "Internal Job Posting": {
    summary:
      "Automated internal job posting pipeline from manager request to ATS publication. A manager request webhook triggers AI job description drafting, inclusive language checking via runCode, company boilerplate formatting, an HR/manager approval gate, a revision loop if rejected, and final requisition creation in Greenhouse ATS.",
    nodes: [
      trigger("Webhook: Manager job posting request", "webhook_app", {
        source: "HRIS / Internal Portal",
        event: "job.posting.request",
        howItStarts:
          "A hiring manager submits a job posting request through the internal portal or HRIS, triggering the automated JD creation pipeline",
        setup:
          "Configure the internal hiring portal or HRIS to POST manager requests to this workflow URL when a new position is requested. Include role details, department, and manager info.",
        inputFields: [],
      }),

      // 2. AI JD draft
      aiNode(
        "2",
        "AI: Draft job description",
        280,
        "You are an expert HR copywriter. Given a manager's job posting request, draft a compelling, structured job description. Include: role title, department, reporting structure, key responsibilities (5-7), required qualifications, preferred qualifications, compensation range context, benefits highlights, and growth opportunities. Use engaging but professional language. Respond JSON: {manager_email: string, manager_name: string, role_title: string, department: string, level: string, location: string, job_description: string, responsibilities: string, required_quals: string, preferred_quals: string, compensation_band: string}",
        "Manager job posting request: {webhook_payload}",
        [
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "role_title", as: "role_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "level", as: "level", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "job_description", as: "job_description", type: "string" },
          { field: "responsibilities", as: "responsibilities", type: "string" },
          { field: "required_quals", as: "required_quals", type: "string" },
          { field: "preferred_quals", as: "preferred_quals", type: "string" },
          {
            field: "compensation_band",
            as: "compensation_band",
            type: "string",
          },
        ],
      ),

      // 3. Inclusive language check (runCode)
      n("3", "runCode", "Check inclusive language & bias", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Scan JD for non-inclusive language and suggest replacements
const jd = vars.job_description || "";
const resp = vars.responsibilities || "";
const reqs = vars.required_quals || "";
const fullText = jd + " " + resp + " " + reqs;

const biasTerms = {
  "rockstar": "high-performer", "ninja": "specialist", "guru": "expert",
  "manpower": "workforce", "chairman": "chairperson", "he/she": "they",
  "young and energetic": "motivated and driven", "digital native": "digitally proficient",
  "culture fit": "culture add", "aggressive": "ambitious", "man hours": "person-hours",
  "master/slave": "primary/replica", "blacklist": "blocklist", "whitelist": "allowlist"
};
const found = [];
const lower = fullText.toLowerCase();
for (const [term, replacement] of Object.entries(biasTerms)) {
  if (lower.includes(term.toLowerCase())) {
    found.push(term + " -> " + replacement);
  }
}
const issueCount = found.length;
const isClean = issueCount === 0 ? "yes" : "no";
const suggestions = found.length > 0 ? found.join("; ") : "No issues found - language is inclusive";

return {
  inclusive_check_passed: isClean,
  bias_issues_count: issueCount,
  bias_suggestions: suggestions,
  cleaned_jd: fullText
};`,
          outputVariables: [
            { name: "inclusive_check_passed", type: "string" },
            { name: "bias_issues_count", type: "number" },
            { name: "bias_suggestions", type: "string" },
            { name: "cleaned_jd", type: "string" },
          ],
        },
      }),

      // 4. Company boilerplate formatting
      aiNode(
        "4",
        "AI: Apply company branding & legal boilerplate",
        760,
        "You are a corporate communications specialist. Take the job description and apply standard company boilerplate: add the company EEO statement, benefits summary, company mission/values snippet, and format in the standard company JD template structure. Incorporate any bias fix suggestions. Respond JSON: {formatted_jd: string, eeo_included: boolean, benefits_section: string, posting_ready: string}",
        "Role: {role_title} ({level}) in {department} at {location}.\\nJD: {job_description}\\nResponsibilities: {responsibilities}\\nRequired: {required_quals}\\nPreferred: {preferred_quals}\\nCompensation: {compensation_band}\\nBias suggestions to apply: {bias_suggestions}",
        [
          { field: "formatted_jd", as: "formatted_jd", type: "string" },
          { field: "eeo_included", as: "eeo_included", type: "string" },
          { field: "benefits_section", as: "benefits_section", type: "string" },
          { field: "posting_ready", as: "posting_ready", type: "string" },
        ],
      ),

      // 5. HR/Manager review approval gate
      n("5", "approvalGate", "HR/Manager: Review & approve JD", 1000, {
        approvalGateData: {
          message:
            "New internal job posting ready for review.\\n\\nRole: {role_title} ({level})\\nDepartment: {department}\\nLocation: {location}\\nManager: {manager_name}\\n\\nInclusive language check: {inclusive_check_passed} ({bias_issues_count} issues)\\nSuggestions applied: {bias_suggestions}\\n\\n--- Job Description ---\\n{formatted_jd}\\n\\nApprove to publish, or reject with revision notes.",
        },
      }),

      // 6. Revision notification if rejected
      emailNode(
        "6",
        "Send revision request to manager",
        1240,
        "{manager_email}",
        "Revision Needed: {role_title} Job Posting",
        "Hi {manager_name},\\n\\nThe job posting for {role_title} ({department}) requires revisions before publication.\\n\\nPlease review the feedback from HR and resubmit with updates. The inclusive language check found {bias_issues_count} issue(s): {bias_suggestions}\\n\\nPlease update the posting and resubmit through the portal.",
        -220,
      ),

      // 7. ATS requisition creation (Greenhouse)
      n("7", "hitApi", "Create requisition in Greenhouse", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/jobs",
          headers: [
            {
              key: "Authorization",
              value: "Basic {greenhouse_api_key_base64}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{role_title}" },
            { key: "department_id", value: "{department}" },
            { key: "status", value: "open" },
            { key: "notes", value: "{formatted_jd}" },
            {
              key: "anywhere",
              value: '{"location":"{location}","level":"{level}"}',
            },
          ],
          variables: [
            { name: "id", customName: "greenhouse_job_id", type: "number" },
            {
              name: "absolute_url",
              customName: "job_posting_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // approved -> publish to Greenhouse
      e("5", "6", "output_2"), // rejected -> revision email to manager
    ],
  },

  // ===================================================================
  // 721. INTERVIEW SCHEDULER (8 nodes)
  // ===================================================================

  "Interview Scheduler": {
    summary:
      "Automated interview scheduling that coordinates panels, checks availability, and books slots. A schedulable stage trigger pulls panel and interview type from Greenhouse, checks Google Calendar availability, optimizes time slots via runCode, routes on no-availability condition, sends the candidate a scheduling link email, creates calendar events for all participants, and updates the ATS stage.",
    nodes: [
      trigger("Webhook: Candidate reaches schedulable stage", "webhook_app", {
        source: "Greenhouse",
        event: "candidate.stage_change",
        howItStarts:
          "A candidate advances to an interview stage in Greenhouse that requires scheduling, triggering the automated coordination flow",
        setup:
          "Configure Greenhouse webhook for candidate.stage_change events. Filter for interview stages (phone screen, onsite, technical, etc.) in the workflow URL or via first-node extraction.",
        inputFields: [],
      }),

      // 2. Panel + type pull from Greenhouse
      aiNode(
        "2",
        "AI: Extract scheduling requirements",
        280,
        "You are an interview scheduling assistant. Parse the Greenhouse webhook to extract candidate details, interview stage, panel members, interview type, and duration. Identify all required interviewers and their emails. Respond JSON: {candidate_email: string, candidate_name: string, application_id: string, job_title: string, interview_stage: string, interview_type: string, duration_minutes: number, panel_emails: string, panel_names: string, panel_count: number, timezone: string, scheduling_window_days: number}",
        "Greenhouse stage change payload: {webhook_payload}",
        [
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "application_id", as: "application_id", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "interview_stage", as: "interview_stage", type: "string" },
          { field: "interview_type", as: "interview_type", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "panel_emails", as: "panel_emails", type: "string" },
          { field: "panel_names", as: "panel_names", type: "string" },
          { field: "panel_count", as: "panel_count", type: "number" },
          { field: "timezone", as: "timezone", type: "string" },
          {
            field: "scheduling_window_days",
            as: "scheduling_window_days",
            type: "number",
          },
        ],
      ),

      // 3. Calendar availability check (Google Calendar)
      n("3", "hitApi", "Check panel availability (Google Calendar)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{scheduling_start_iso}" },
            { key: "timeMax", value: "{scheduling_end_iso}" },
            { key: "timeZone", value: "{timezone}" },
            { key: "items", value: "{panel_emails}" },
          ],
          variables: [
            {
              name: "calendars",
              customName: "calendar_busy_data",
              type: "string",
            },
          ],
        },
      }),

      // 4. Slot optimization (runCode)
      n("4", "runCode", "Find optimal interview slots", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Find overlapping free windows for all panelists
const busyData = JSON.parse(vars.calendar_busy_data || "{}");
const duration = parseInt(vars.duration_minutes) || 60;
const panelEmails = (vars.panel_emails || "").split(",").map(e => e.trim());
const windowDays = parseInt(vars.scheduling_window_days) || 5;

// Generate candidate slots (9am-5pm working hours)
const slots = [];
const now = new Date();
for (let d = 1; d <= windowDays; d++) {
  const day = new Date(now);
  day.setDate(day.getDate() + d);
  if (day.getDay() === 0 || day.getDay() === 6) continue; // skip weekends
  for (let h = 9; h <= 16; h++) {
    const start = new Date(day);
    start.setHours(h, 0, 0, 0);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + duration);

    // Check all panelists are free
    let allFree = true;
    for (const email of panelEmails) {
      const busyPeriods = (busyData[email] || {}).busy || [];
      for (const b of busyPeriods) {
        const bs = new Date(b.start);
        const be = new Date(b.end);
        if (start < be && end > bs) { allFree = false; break; }
      }
      if (!allFree) break;
    }
    if (allFree) {
      slots.push({ start: start.toISOString(), end: end.toISOString(), display: start.toLocaleDateString() + " " + start.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"}) });
    }
  }
}
const topSlots = slots.slice(0, 5);
const hasAvailability = topSlots.length > 0 ? "yes" : "no";
const slotOptions = topSlots.map((s, i) => (i + 1) + ". " + s.display).join("\\n");
const bestSlotStart = topSlots.length > 0 ? topSlots[0].start : "";
const bestSlotEnd = topSlots.length > 0 ? topSlots[0].end : "";

return {
  has_availability: hasAvailability,
  available_slot_count: topSlots.length,
  slot_options: slotOptions,
  best_slot_start: bestSlotStart,
  best_slot_end: bestSlotEnd,
  slots_json: JSON.stringify(topSlots)
};`,
          outputVariables: [
            { name: "has_availability", type: "string" },
            { name: "available_slot_count", type: "number" },
            { name: "slot_options", type: "string" },
            { name: "best_slot_start", type: "string" },
            { name: "best_slot_end", type: "string" },
            { name: "slots_json", type: "string" },
          ],
        },
      }),

      // 5. No-availability condition
      n("5", "condition", "Available slots found?", 1000, {
        conditionData: [
          {
            variable: "has_availability",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Candidate scheduling link email — True branch
      emailNode(
        "6",
        "Send scheduling options to candidate",
        1240,
        "{candidate_email}",
        "Interview Scheduling: {job_title} - {interview_stage}",
        "Hi {candidate_name},\\n\\nCongratulations on advancing to the {interview_stage} stage for {job_title}!\\n\\nYour {interview_type} interview ({duration_minutes} minutes) is ready to be scheduled. Here are the available time slots:\\n\\n{slot_options}\\n\\nPlease select your preferred time by replying to this email or using the scheduling link below.\\n\\nPanel: {panel_names}\\n\\nWe look forward to speaking with you!",
      ),

      // 7. Calendar event creation
      n("7", "hitApi", "Create calendar event (Google Calendar)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?sendUpdates=all",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "summary",
              value: "{interview_stage}: {candidate_name} - {job_title}",
            },
            {
              key: "start",
              value: '{"dateTime":"{best_slot_start}","timeZone":"{timezone}"}',
            },
            {
              key: "end",
              value: '{"dateTime":"{best_slot_end}","timeZone":"{timezone}"}',
            },
            { key: "attendees", value: "{panel_emails},{candidate_email}" },
            {
              key: "description",
              value:
                "Interview: {interview_type}\\nCandidate: {candidate_name}\\nRole: {job_title}\\nStage: {interview_stage}\\nDuration: {duration_minutes} min",
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            {
              name: "htmlLink",
              customName: "calendar_event_link",
              type: "string",
            },
          ],
        },
      }),

      // 8. ATS update
      n("8", "hitApi", "Update ATS interview status (Greenhouse)", 1720, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://harvest.greenhouse.io/v1/applications/{application_id}/scheduled_interviews",
          headers: [
            {
              key: "Authorization",
              value: "Basic {greenhouse_api_key_base64}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "status", value: "scheduled" },
            { key: "start", value: "{best_slot_start}" },
            { key: "end", value: "{best_slot_end}" },
            { key: "external_event_id", value: "{calendar_event_id}" },
          ],
          variables: [
            {
              name: "id",
              customName: "scheduled_interview_id",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // slots available -> send to candidate
      e("5", "8", "output_2"), // no slots -> update ATS as unschedulable
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 722. INTERVIEW SCHEDULER BOT (8 nodes)
  // ===================================================================

  "Interview Scheduler Bot": {
    summary:
      "Conversational bot for coordinators/recruiters to schedule interviews in real-time. A chat trigger clarifies participants and type via AI, checks real-time Google Calendar availability, resolves conflicts via runCode condition, presents available slots via sendMessage, confirms the booking, sends participant notification emails, and handles reschedule requests.",
    nodes: [
      trigger("Chat trigger: Interview scheduling request", "webhook_form", {
        source: "Slack / Internal Portal",
        event: "interview.schedule.request",
        howItStarts:
          "A recruiter or coordinator asks the scheduling bot to set up an interview via chat, Slack command, or portal widget",
        setup:
          "Configure Slack bot or internal portal chat widget to POST scheduling requests to this workflow URL. The bot will conversationally clarify requirements.",
        inputFields: [],
      }),

      // 2. AI clarification (who/how long/type)
      aiNode(
        "2",
        "AI: Clarify scheduling requirements",
        280,
        "You are an interview scheduling assistant chatbot. Parse the coordinator's request to understand: which candidate, which interviewers, interview type (phone/video/onsite), desired duration, preferred date range, any special requirements (room booking, accessibility). Extract all participant emails. Respond JSON: {coordinator_email: string, candidate_name: string, candidate_email: string, interviewer_emails: string, interviewer_names: string, interview_type: string, duration_minutes: number, preferred_dates: string, special_requirements: string, timezone: string, is_reschedule: string, original_event_id: string}",
        "Scheduling request: {webhook_payload}",
        [
          {
            field: "coordinator_email",
            as: "coordinator_email",
            type: "string",
          },
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          {
            field: "interviewer_emails",
            as: "interviewer_emails",
            type: "string",
          },
          {
            field: "interviewer_names",
            as: "interviewer_names",
            type: "string",
          },
          { field: "interview_type", as: "interview_type", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "preferred_dates", as: "preferred_dates", type: "string" },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
          { field: "timezone", as: "timezone", type: "string" },
          { field: "is_reschedule", as: "is_reschedule", type: "string" },
          {
            field: "original_event_id",
            as: "original_event_id",
            type: "string",
          },
        ],
      ),

      // 3. Real-time availability check (Google Calendar)
      n("3", "hitApi", "Check real-time availability (Google Calendar)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{preferred_dates_start}" },
            { key: "timeMax", value: "{preferred_dates_end}" },
            { key: "timeZone", value: "{timezone}" },
            { key: "items", value: "{interviewer_emails},{candidate_email}" },
          ],
          variables: [
            {
              name: "calendars",
              customName: "availability_data",
              type: "string",
            },
          ],
        },
      }),

      // 4. Conflict resolution (runCode)
      n("4", "runCode", "Resolve conflicts & find open slots", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse availability and find overlapping free windows
const avail = JSON.parse(vars.availability_data || "{}");
const duration = parseInt(vars.duration_minutes) || 60;
const allEmails = (vars.interviewer_emails + "," + vars.candidate_email).split(",").map(e => e.trim()).filter(Boolean);

const slots = [];
const now = new Date();
for (let d = 0; d < 7; d++) {
  const day = new Date(now);
  day.setDate(day.getDate() + d + 1);
  if (day.getDay() === 0 || day.getDay() === 6) continue;
  for (let h = 9; h <= 17; h++) {
    for (let m = 0; m < 60; m += 30) {
      const start = new Date(day);
      start.setHours(h, m, 0, 0);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + duration);
      if (end.getHours() > 17 || (end.getHours() === 17 && end.getMinutes() > 0)) continue;

      let allFree = true;
      for (const email of allEmails) {
        const busyPeriods = (avail[email] || {}).busy || [];
        for (const b of busyPeriods) {
          if (start < new Date(b.end) && end > new Date(b.start)) { allFree = false; break; }
        }
        if (!allFree) break;
      }
      if (allFree) slots.push({ start: start.toISOString(), end: end.toISOString(), label: start.toLocaleDateString("en-US", {weekday:"short", month:"short", day:"numeric"}) + " at " + start.toLocaleTimeString("en-US", {hour:"2-digit", minute:"2-digit"}) });
    }
  }
}
const top = slots.slice(0, 6);
const hasConflict = top.length === 0 ? "yes" : "no";
const slotDisplay = top.map((s, i) => (i+1) + ". " + s.label).join("\\n");
return {
  has_conflict: hasConflict,
  available_slots: slotDisplay,
  slot_count: top.length,
  selected_start: top.length > 0 ? top[0].start : "",
  selected_end: top.length > 0 ? top[0].end : "",
  selected_label: top.length > 0 ? top[0].label : "No available slots",
  all_slots_json: JSON.stringify(top)
};`,
          outputVariables: [
            { name: "has_conflict", type: "string" },
            { name: "available_slots", type: "string" },
            { name: "slot_count", type: "number" },
            { name: "selected_start", type: "string" },
            { name: "selected_end", type: "string" },
            { name: "selected_label", type: "string" },
            { name: "all_slots_json", type: "string" },
          ],
        },
      }),

      // 5. Slot presentation (sendMessage)
      n("5", "sendMessage", "Present available slots", 1000, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "Here are the available slots for {candidate_name}'s {interview_type} interview ({duration_minutes} min) with {interviewer_names}:\\n\\n{available_slots}\\n\\nReply with the number of your preferred slot, or say 'more options' to see additional times.",
        },
      }),

      // 6. Booking confirmation
      n("6", "hitApi", "Create calendar booking (Google Calendar)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?sendUpdates=all",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "summary",
              value: "{interview_type} Interview: {candidate_name}",
            },
            {
              key: "start",
              value: '{"dateTime":"{selected_start}","timeZone":"{timezone}"}',
            },
            {
              key: "end",
              value: '{"dateTime":"{selected_end}","timeZone":"{timezone}"}',
            },
            {
              key: "attendees",
              value: "{interviewer_emails},{candidate_email}",
            },
            {
              key: "description",
              value:
                "Interview Type: {interview_type}\\nDuration: {duration_minutes} min\\nCandidate: {candidate_name}\\nInterviewers: {interviewer_names}\\nSpecial Requirements: {special_requirements}",
            },
          ],
          variables: [
            { name: "id", customName: "event_id", type: "string" },
            { name: "htmlLink", customName: "event_link", type: "string" },
          ],
        },
      }),

      // 7. Participant notification (email)
      emailNode(
        "7",
        "Notify all participants",
        1480,
        "{candidate_email}",
        "Interview Confirmed: {interview_type} - {selected_label}",
        "Hi {candidate_name},\\n\\nYour {interview_type} interview has been confirmed!\\n\\nDate/Time: {selected_label}\\nDuration: {duration_minutes} minutes\\nInterviewers: {interviewer_names}\\n\\nCalendar invite: {event_link}\\n\\n{special_requirements}\\n\\nPlease let us know if you need to reschedule.",
      ),

      // 8. Reschedule handling
      n("8", "condition", "Is this a reschedule?", 1720, {
        conditionData: [
          {
            variable: "is_reschedule",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "6", "output_1"), // reschedule -> re-book
    ],
  },

  // ===================================================================
  // 723. JOB APPLICATION BOT (8 nodes)
  // ===================================================================

  "Job Application Bot": {
    summary:
      "Conversational bot that guides candidates through the application process. A chat trigger kicks off AI-guided information collection, handles resume upload and parsing, validates ATS fields via runCode, checks completeness via condition, submits to Greenhouse ATS, sends a candidate confirmation email, and alerts the recruiter via Slack.",
    nodes: [
      trigger("Chat trigger: Job application started", "webhook_form", {
        source: "Career Site / Chat Widget",
        event: "application.started",
        howItStarts:
          "A candidate initiates a job application via the career site chatbot, clicking 'Apply Now' on a specific job listing",
        setup:
          "Embed chat widget on career site job pages. Configure to POST candidate interactions and file uploads to this workflow URL. Include the job_id in the initial payload.",
        inputFields: [],
      }),

      // 2. AI guided collection
      aiNode(
        "2",
        "AI: Guide application & extract data",
        280,
        "You are a friendly job application assistant. Parse the candidate's submission to extract: full name, email, phone, current title, years of experience, education, relevant skills, desired salary range, start date availability, and the job they are applying for. If a resume text or file reference is included, extract structured data from it. Respond JSON: {candidate_name: string, candidate_email: string, candidate_phone: string, current_title: string, years_experience: number, education: string, skills: string, salary_expectation: string, start_availability: string, job_id: string, job_title: string, resume_text: string, cover_letter: string, linkedin_url: string}",
        "Application submission: {webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "candidate_phone", as: "candidate_phone", type: "string" },
          { field: "current_title", as: "current_title", type: "string" },
          { field: "years_experience", as: "years_experience", type: "number" },
          { field: "education", as: "education", type: "string" },
          { field: "skills", as: "skills", type: "string" },
          {
            field: "salary_expectation",
            as: "salary_expectation",
            type: "string",
          },
          {
            field: "start_availability",
            as: "start_availability",
            type: "string",
          },
          { field: "job_id", as: "job_id", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "resume_text", as: "resume_text", type: "string" },
          { field: "cover_letter", as: "cover_letter", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
        ],
      ),

      // 3. Resume upload + parsing
      aiNode(
        "3",
        "AI: Parse & structure resume",
        520,
        "You are a resume parsing specialist. Given the candidate's resume text, extract structured sections: work history (company, title, dates, achievements), education (institution, degree, year), certifications, skills categorized (technical, soft, languages), and a brief professional summary. Respond JSON: {work_history: string, education_structured: string, certifications: string, technical_skills: string, soft_skills: string, languages: string, professional_summary: string, total_roles: number}",
        "Resume content: {resume_text}. Candidate: {candidate_name}. Current title: {current_title}.",
        [
          { field: "work_history", as: "work_history", type: "string" },
          {
            field: "education_structured",
            as: "education_structured",
            type: "string",
          },
          { field: "certifications", as: "certifications", type: "string" },
          { field: "technical_skills", as: "technical_skills", type: "string" },
          { field: "soft_skills", as: "soft_skills", type: "string" },
          { field: "languages", as: "languages_spoken", type: "string" },
          {
            field: "professional_summary",
            as: "professional_summary",
            type: "string",
          },
          { field: "total_roles", as: "total_roles", type: "number" },
        ],
      ),

      // 4. ATS field validation (runCode)
      n("4", "runCode", "Validate application completeness", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate all required ATS fields are populated
const required = {
  candidate_name: vars.candidate_name || "",
  candidate_email: vars.candidate_email || "",
  job_id: vars.job_id || "",
  resume_text: vars.resume_text || "",
};
const missing = [];
for (const [field, val] of Object.entries(required)) {
  if (!val || val.trim() === "") missing.push(field);
}
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
const emailValid = emailRegex.test(vars.candidate_email || "");
if (!emailValid) missing.push("valid_email_format");

const isComplete = missing.length === 0 ? "yes" : "no";
const missingFields = missing.join(", ");
const completenessScore = Math.round(((Object.keys(required).length - missing.length) / Object.keys(required).length) * 100);

// Optional field richness check
const optional = [vars.candidate_phone, vars.cover_letter, vars.linkedin_url, vars.education, vars.salary_expectation];
const optionalFilled = optional.filter(v => v && v.trim() !== "").length;

return {
  is_complete: isComplete,
  missing_fields: missingFields,
  completeness_score: completenessScore,
  optional_fields_filled: optionalFilled,
  validation_summary: isComplete ? "Application complete and ready for submission" : "Missing required fields: " + missingFields
};`,
          outputVariables: [
            { name: "is_complete", type: "string" },
            { name: "missing_fields", type: "string" },
            { name: "completeness_score", type: "number" },
            { name: "optional_fields_filled", type: "number" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 5. Completeness condition
      n("5", "condition", "Application complete?", 1000, {
        conditionData: [
          {
            variable: "is_complete",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. ATS submission (Greenhouse) — True branch
      n("6", "hitApi", "Submit application to Greenhouse", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/candidates",
          headers: [
            {
              key: "Authorization",
              value: "Basic {greenhouse_api_key_base64}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "first_name", value: "{candidate_name}" },
            {
              key: "email_addresses",
              value: '[{"value":"{candidate_email}","type":"personal"}]',
            },
            {
              key: "phone_numbers",
              value: '[{"value":"{candidate_phone}","type":"mobile"}]',
            },
            { key: "applications", value: '[{"job_id":{job_id}}]' },
            {
              key: "website_addresses",
              value: '[{"value":"{linkedin_url}","type":"LinkedIn"}]',
            },
            { key: "tags", value: '["chatbot-application"]' },
            {
              key: "custom_fields",
              value:
                '{"salary_expectation":"{salary_expectation}","start_availability":"{start_availability}"}',
            },
          ],
          variables: [
            {
              name: "id",
              customName: "greenhouse_candidate_id",
              type: "number",
            },
            {
              name: "applications[0].id",
              customName: "greenhouse_application_id",
              type: "number",
            },
          ],
        },
      }),

      // 7. Candidate confirmation email
      emailNode(
        "7",
        "Send application confirmation to candidate",
        1480,
        "{candidate_email}",
        "Application Received: {job_title}",
        "Hi {candidate_name},\\n\\nThank you for applying for {job_title}!\\n\\nWe've received your application and our recruiting team will review it shortly.\\n\\nApplication Summary:\\n- Position: {job_title}\\n- Experience: {years_experience} years\\n- Current Role: {current_title}\\n- Skills: {technical_skills}\\n\\nYou'll hear from us within 5-7 business days. In the meantime, feel free to reach out if you have any questions.\\n\\nBest regards,\\nThe Recruiting Team",
      ),

      // 8. Recruiter Slack alert
      slackNode(
        "8",
        "Alert recruiter (Slack)",
        1720,
        ":new: *New Application Received*\\nCandidate: {candidate_name} ({candidate_email})\\nRole: {job_title}\\nExperience: {years_experience} years | Current: {current_title}\\nSkills: {technical_skills}\\nCompleteness: {completeness_score}% | Optional fields: {optional_fields_filled}/5\\nGreenhouse ID: {greenhouse_candidate_id}\\nSummary: {professional_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // complete -> submit to ATS
      e("5", "7", "output_2"), // incomplete -> send email asking for missing info
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 724. JOB BOARD MULTI-POSTER (9 nodes)
  // ===================================================================

  "Job Board Multi-Poster": {
    summary:
      "Distributes optimized job postings across multiple platforms simultaneously. A distribution trigger fires AI description optimization, platform-specific adaptation via runCode, LinkedIn Job Posting API distribution, Indeed Job Sync, ATS/multiposter distribution, publication verification, recruiter Slack notification, and Sheets-based performance tracking.",
    nodes: [
      trigger("Webhook: Job approved for distribution", "webhook_app", {
        source: "Greenhouse / HRIS",
        event: "job.approved_for_posting",
        howItStarts:
          "A job description is approved and marked for external distribution, triggering multi-platform posting",
        setup:
          "Configure Greenhouse or HRIS to fire a webhook when a job posting is approved for external distribution. Include the full JD, requirements, compensation, and department metadata.",
        inputFields: [],
      }),

      // 2. AI description optimization
      aiNode(
        "2",
        "AI: Optimize JD for job boards",
        280,
        "You are a job posting optimization expert. Given an approved job description, optimize it for maximum visibility and applicant quality across job boards. Improve SEO keywords, strengthen the value proposition, ensure mobile-friendly formatting, and create platform-specific hooks. Respond JSON: {job_id: string, job_title: string, department: string, location: string, employment_type: string, salary_min: string, salary_max: string, currency: string, optimized_description: string, seo_keywords: string, short_description: string, recruiter_email: string, company_name: string}",
        "Approved job posting payload: {webhook_payload}",
        [
          { field: "job_id", as: "job_id", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "employment_type", as: "employment_type", type: "string" },
          { field: "salary_min", as: "salary_min", type: "string" },
          { field: "salary_max", as: "salary_max", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          {
            field: "optimized_description",
            as: "optimized_description",
            type: "string",
          },
          { field: "seo_keywords", as: "seo_keywords", type: "string" },
          {
            field: "short_description",
            as: "short_description",
            type: "string",
          },
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
        ],
      ),

      // 3. Platform-specific adaptation (runCode)
      n("3", "runCode", "Adapt content for each platform", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate platform-specific versions of the job posting
const title = vars.job_title || "";
const desc = vars.optimized_description || "";
const shortDesc = vars.short_description || "";
const keywords = vars.seo_keywords || "";
const location = vars.location || "";
const salaryMin = vars.salary_min || "";
const salaryMax = vars.salary_max || "";
const type = vars.employment_type || "full-time";
const company = vars.company_name || "";

// LinkedIn format: Rich, professional, max 2000 chars for description
const linkedinDesc = desc.substring(0, 2000);
const linkedinTitle = title.length > 100 ? title.substring(0, 97) + "..." : title;

// Indeed format: Structured with clear sections, salary transparency
const indeedDesc = "About " + company + ":\\n\\n" + desc + "\\n\\nCompensation: " + salaryMin + " - " + salaryMax + " " + (vars.currency || "USD") + "\\nType: " + type + "\\nLocation: " + location;

// General board format: SEO-optimized
const generalDesc = title + " | " + company + " | " + location + "\\n\\n" + desc + "\\n\\nKeywords: " + keywords;

return {
  linkedin_title: linkedinTitle,
  linkedin_description: linkedinDesc,
  indeed_description: indeedDesc,
  general_description: generalDesc,
  platforms_targeted: "LinkedIn, Indeed, General ATS boards",
  platform_count: 3
};`,
          outputVariables: [
            { name: "linkedin_title", type: "string" },
            { name: "linkedin_description", type: "string" },
            { name: "indeed_description", type: "string" },
            { name: "general_description", type: "string" },
            { name: "platforms_targeted", type: "string" },
            { name: "platform_count", type: "number" },
          ],
        },
      }),

      // 4. LinkedIn Job Posting API
      n("4", "hitApi", "Post to LinkedIn Jobs", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/simpleJobPostings",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "integrationContext", value: "{company_name}" },
            { key: "title", value: "{linkedin_title}" },
            { key: "description", value: "{linkedin_description}" },
            { key: "location", value: "{location}" },
            { key: "listedAt", value: "{current_timestamp}" },
            { key: "employmentStatus", value: "{employment_type}" },
          ],
          variables: [
            { name: "id", customName: "linkedin_posting_id", type: "string" },
            {
              name: "applyUrl",
              customName: "linkedin_apply_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. Indeed Job Sync
      n("5", "hitApi", "Sync to Indeed", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://apis.indeed.com/v2/jobs",
          headers: [
            { key: "Authorization", value: "Bearer {indeed_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{job_title}" },
            { key: "description", value: "{indeed_description}" },
            { key: "location", value: "{location}" },
            {
              key: "salary",
              value:
                '{"min":{salary_min},"max":{salary_max},"currency":"{currency}","period":"yearly"}',
            },
            { key: "jobType", value: "{employment_type}" },
          ],
          variables: [
            { name: "jobKey", customName: "indeed_job_key", type: "string" },
            { name: "url", customName: "indeed_posting_url", type: "string" },
          ],
        },
      }),

      // 6. ATS/multiposter distribution
      n("6", "hitApi", "Distribute via ATS multiposter", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/jobs/{job_id}/job_posts",
          headers: [
            {
              key: "Authorization",
              value: "Basic {greenhouse_api_key_base64}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{job_title}" },
            { key: "content", value: "{general_description}" },
            { key: "external", value: "true" },
            { key: "active", value: "true" },
          ],
          variables: [
            { name: "id", customName: "greenhouse_post_id", type: "number" },
            {
              name: "live",
              customName: "greenhouse_post_live",
              type: "boolean",
            },
          ],
        },
      }),

      // 7. Publication verification (runCode)
      n("7", "runCode", "Verify all publications succeeded", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify posting results across all platforms
const results = [];
const linkedinOk = vars.linkedin_posting_id ? true : false;
const indeedOk = vars.indeed_job_key ? true : false;
const greenhouseOk = vars.greenhouse_post_live ? true : false;

if (linkedinOk) results.push("LinkedIn: OK (ID: " + vars.linkedin_posting_id + ")");
else results.push("LinkedIn: FAILED");
if (indeedOk) results.push("Indeed: OK (Key: " + vars.indeed_job_key + ")");
else results.push("Indeed: FAILED");
if (greenhouseOk) results.push("Greenhouse: OK (ID: " + vars.greenhouse_post_id + ")");
else results.push("Greenhouse: FAILED");

const successCount = [linkedinOk, indeedOk, greenhouseOk].filter(Boolean).length;
const allSucceeded = successCount === 3 ? "yes" : "no";

return {
  publication_results: results.join("; "),
  success_count: successCount,
  total_platforms: 3,
  all_succeeded: allSucceeded,
  failed_platforms: [!linkedinOk && "LinkedIn", !indeedOk && "Indeed", !greenhouseOk && "Greenhouse"].filter(Boolean).join(", ") || "None"
};`,
          outputVariables: [
            { name: "publication_results", type: "string" },
            { name: "success_count", type: "number" },
            { name: "total_platforms", type: "number" },
            { name: "all_succeeded", type: "string" },
            { name: "failed_platforms", type: "string" },
          ],
        },
      }),

      // 8. Recruiter notification (Slack)
      slackNode(
        "8",
        "Notify recruiter (Slack)",
        1720,
        ":mega: *Job Posted to {success_count}/{total_platforms} Platforms*\\nRole: {job_title} ({department})\\nLocation: {location} | Type: {employment_type}\\nSalary: {salary_min}-{salary_max} {currency}\\n\\nResults: {publication_results}\\n\\nLinkedIn: {linkedin_apply_url}\\nIndeed: {indeed_posting_url}\\nFailed: {failed_platforms}",
      ),

      // 9. Performance tracking (Sheets)
      sheetsNode(
        "9",
        "Log posting to tracker (Sheets)",
        1960,
        "Job_Postings",
        '[["{job_id}","{job_title}","{department}","{location}","{platforms_targeted}","{success_count}","{failed_platforms}","{linkedin_posting_id}","{indeed_job_key}","{greenhouse_post_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 725. JOB DESCRIPTION GENERATOR (7 nodes)
  // ===================================================================

  "Job Description Generator": {
    summary:
      "AI-powered JD creation pipeline with inclusive language auditing. A hiring request trigger feeds AI JD drafting, inclusive language and bias checking via runCode, branding and legal boilerplate formatting, an HR/manager approval gate, a revision loop for rejected drafts, and final ATS publication to Greenhouse.",
    nodes: [
      trigger("Webhook: Hiring request submitted", "webhook_app", {
        source: "HRIS / Internal Portal",
        event: "hiring.request.submitted",
        howItStarts:
          "A hiring manager or HR partner submits a new role request with basic requirements, triggering the JD generation pipeline",
        setup:
          "Configure internal hiring request form or HRIS to POST new role requests to this workflow URL. Include role title, department, level, team size, key requirements, and hiring manager contact.",
        inputFields: [],
      }),

      // 2. AI JD draft
      aiNode(
        "2",
        "AI: Generate job description",
        280,
        "You are a senior talent acquisition copywriter. Given a hiring request, generate a comprehensive, engaging job description. Structure: compelling intro (2-3 sentences), role impact statement, key responsibilities (6-8 bullets), required qualifications (5-6 bullets), preferred qualifications (3-4 bullets), what we offer section, and growth opportunity note. Use action verbs, quantify where possible, and avoid jargon. Respond JSON: {manager_email: string, manager_name: string, role_title: string, department: string, level: string, team_size: number, location: string, remote_policy: string, job_description_full: string, intro_paragraph: string, responsibilities_section: string, required_section: string, preferred_section: string, benefits_section: string}",
        "Hiring request payload: {webhook_payload}",
        [
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "role_title", as: "role_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "level", as: "level", type: "string" },
          { field: "team_size", as: "team_size", type: "number" },
          { field: "location", as: "location", type: "string" },
          { field: "remote_policy", as: "remote_policy", type: "string" },
          {
            field: "job_description_full",
            as: "job_description_full",
            type: "string",
          },
          {
            field: "responsibilities_section",
            as: "responsibilities_section",
            type: "string",
          },
          { field: "required_section", as: "required_section", type: "string" },
          {
            field: "preferred_section",
            as: "preferred_section",
            type: "string",
          },
          { field: "benefits_section", as: "benefits_section", type: "string" },
        ],
      ),

      // 3. Inclusive language + bias check (runCode)
      n("3", "runCode", "Audit for bias & inclusive language", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Comprehensive bias and inclusivity audit
const text = (vars.job_description_full || "").toLowerCase();

const genderTerms = {"he/she":"they","his/her":"their","manpower":"workforce","salesman":"sales representative","chairman":"chairperson","foreman":"supervisor","mankind":"humanity"};
const ageTerms = {"young":"","digital native":"digitally proficient","energetic":"motivated","recent graduate":"early-career professional"};
const abilityTerms = {"able-bodied":"","stand for long periods":"perform physical tasks with or without accommodation","walk":"move throughout"};
const cultureTerms = {"culture fit":"culture add","rockstar":"top performer","ninja":"specialist","guru":"expert","crush it":"excel","kill it":"succeed","aggressive":"ambitious","dominant":"leading"};

const allTerms = {...genderTerms, ...ageTerms, ...abilityTerms, ...cultureTerms};
const issues = [];
for (const [term, fix] of Object.entries(allTerms)) {
  if (text.includes(term)) {
    issues.push({ found: term, suggestion: fix || "Remove or rephrase", category: genderTerms[term] !== undefined ? "gender" : ageTerms[term] !== undefined ? "age" : abilityTerms[term] !== undefined ? "ability" : "culture" });
  }
}

// Check for excessive requirements (may discourage diverse candidates)
const reqText = vars.required_section || "";
const reqBullets = (reqText.match(/•|-|\\*/g) || []).length;
const tooManyReqs = reqBullets > 8;
if (tooManyReqs) issues.push({ found: reqBullets + " required qualifications", suggestion: "Reduce to 5-6 to avoid discouraging diverse candidates", category: "inclusion" });

const score = Math.max(0, 100 - issues.length * 12);
const issueList = issues.map(i => i.found + " (" + i.category + ") -> " + i.suggestion).join("; ");

return {
  bias_score: score,
  issue_count: issues.length,
  issues_detail: issueList || "No issues found - JD passes inclusivity audit",
  needs_revision: issues.length > 0 ? "yes" : "no",
  categories_flagged: [...new Set(issues.map(i => i.category))].join(", ") || "none"
};`,
          outputVariables: [
            { name: "bias_score", type: "number" },
            { name: "issue_count", type: "number" },
            { name: "issues_detail", type: "string" },
            { name: "needs_revision", type: "string" },
            { name: "categories_flagged", type: "string" },
          ],
        },
      }),

      // 4. Branding/legal boilerplate
      aiNode(
        "4",
        "AI: Apply branding & legal boilerplate",
        760,
        "You are a corporate brand compliance specialist. Take the job description and apply company branding: add the standard header with logo reference, company mission snippet, EEO/non-discrimination statement, benefits package summary, application instructions, and privacy notice. If bias issues were found, apply the suggested fixes. Respond JSON: {final_jd: string, word_count: number, reading_level: string, eeo_statement_added: boolean, posting_ready: string}",
        "Role: {role_title} ({level}) in {department}, {location} ({remote_policy}).\\nFull JD: {job_description_full}\\nBias audit: {issues_detail} (Score: {bias_score}/100)\\nBenefits: {benefits_section}",
        [
          { field: "final_jd", as: "final_jd", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "reading_level", as: "reading_level", type: "string" },
          {
            field: "eeo_statement_added",
            as: "eeo_statement_added",
            type: "string",
          },
          { field: "posting_ready", as: "posting_ready", type: "string" },
        ],
      ),

      // 5. HR/Manager approval gate
      n("5", "approvalGate", "HR/Manager: Review final JD", 1000, {
        approvalGateData: {
          message:
            "Job Description ready for approval.\\n\\nRole: {role_title} ({level})\\nDepartment: {department} | Location: {location} ({remote_policy})\\nManager: {manager_name}\\n\\nBias Score: {bias_score}/100 | Issues: {issue_count}\\nWord Count: {word_count} | Reading Level: {reading_level}\\n\\n--- Final JD ---\\n{final_jd}\\n\\nApprove to publish to Greenhouse, or reject with revision notes.",
        },
      }),

      // 6. Revision loop — rejected branch
      emailNode(
        "6",
        "Send revision request",
        1240,
        "{manager_email}",
        "JD Revision Needed: {role_title}",
        "Hi {manager_name},\\n\\nThe job description for {role_title} needs revisions before we can publish.\\n\\nBias audit found {issue_count} issue(s): {issues_detail}\\n\\nPlease review and resubmit through the hiring portal.\\n\\nCurrent bias score: {bias_score}/100\\nCategories flagged: {categories_flagged}",
        -220,
      ),

      // 7. ATS publication (Greenhouse) — approved branch
      n("7", "hitApi", "Publish to Greenhouse ATS", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/jobs",
          headers: [
            {
              key: "Authorization",
              value: "Basic {greenhouse_api_key_base64}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{role_title}" },
            { key: "department_id", value: "{department}" },
            { key: "notes", value: "{final_jd}" },
            { key: "status", value: "open" },
          ],
          variables: [
            { name: "id", customName: "greenhouse_job_id", type: "number" },
            {
              name: "absolute_url",
              customName: "greenhouse_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // approved -> publish
      e("5", "6", "output_2"), // rejected -> revision email
    ],
  },

  // ===================================================================
  // 726. JOB POSTING MULTI CHANNEL PUBLISHER (8 nodes)
  // ===================================================================

  "Job Posting Multi Channel Publisher": {
    summary:
      "Publishes approved JDs across internal boards, external sites, and social channels. A JD-approved trigger selects channels via runCode, adapts content for each platform, posts internally, distributes to external boards, schedules social amplification, tracks all postings in Sheets, and alerts on channel failures via Slack.",
    nodes: [
      trigger("Webhook: JD approved for publication", "webhook_app", {
        source: "Greenhouse / HR Portal",
        event: "jd.approved",
        howItStarts:
          "A job description passes final approval and is marked for multi-channel publication",
        setup:
          "Configure Greenhouse or HR approval workflow to fire a webhook when a JD reaches 'approved' status. Include the full JD, target channels, department, and recruiter info.",
        inputFields: [],
      }),

      // 2. Channel selection (runCode)
      n("2", "runCode", "Select publication channels", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse approval payload and determine publication channels
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data || payload;
const jobTitle = data.job_title || data.title || data.name || "";
const jobId = data.job_id || data.id || "";
const department = data.department || "";
const location = data.location || "";
const level = data.level || data.seniority || "";
const jdContent = data.description || data.jd || data.content || "";
const recruiterEmail = data.recruiter_email || data.recruiter || "";
const isRemote = (location || "").toLowerCase().includes("remote");
const isSenior = (level || "").toLowerCase().match(/senior|lead|director|vp|head|chief|principal/) !== null;

const channels = ["internal_board"];
channels.push("greenhouse_careers");
if (isSenior) channels.push("linkedin_premium");
else channels.push("linkedin_standard");
channels.push("indeed");
if (isRemote) channels.push("remote_boards");
channels.push("social_media");

return {
  job_id: jobId,
  job_title: jobTitle,
  department: department,
  location: location,
  level: level,
  jd_content: jdContent,
  recruiter_email: recruiterEmail,
  selected_channels: channels.join(", "),
  channel_count: channels.length,
  is_senior: isSenior ? "yes" : "no",
  is_remote: isRemote ? "yes" : "no"
};`,
          outputVariables: [
            { name: "job_id", type: "string" },
            { name: "job_title", type: "string" },
            { name: "department", type: "string" },
            { name: "location", type: "string" },
            { name: "level", type: "string" },
            { name: "jd_content", type: "string" },
            { name: "recruiter_email", type: "string" },
            { name: "selected_channels", type: "string" },
            { name: "channel_count", type: "number" },
            { name: "is_senior", type: "string" },
            { name: "is_remote", type: "string" },
          ],
        },
      }),

      // 3. Content adaptation
      aiNode(
        "3",
        "AI: Adapt content per channel",
        520,
        "You are a recruitment marketing specialist. Adapt the job description for multiple channels: create a concise internal posting (focus on growth/mobility), a full external posting (compelling, SEO-optimized), and social media copy (LinkedIn post: professional/engaging, Twitter-length teaser). Respond JSON: {internal_post: string, external_post: string, linkedin_social_post: string, twitter_teaser: string, hashtags: string, apply_url: string}",
        "Job: {job_title} ({level}) in {department}, {location}. Channels: {selected_channels}. JD: {jd_content}",
        [
          { field: "internal_post", as: "internal_post", type: "string" },
          { field: "external_post", as: "external_post", type: "string" },
          {
            field: "linkedin_social_post",
            as: "linkedin_social_post",
            type: "string",
          },
          { field: "twitter_teaser", as: "twitter_teaser", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
          { field: "apply_url", as: "apply_url", type: "string" },
        ],
      ),

      // 4. Internal posting
      slackNode(
        "4",
        "Post to internal job board (Slack)",
        760,
        ":briefcase: *New Internal Opportunity*\\n\\n*{job_title}* ({level})\\nDepartment: {department} | Location: {location}\\n\\n{internal_post}\\n\\nApply: {apply_url}",
      ),

      // 5. External board distribution
      n("5", "hitApi", "Distribute to external boards", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/jobs/{job_id}/job_posts",
          headers: [
            {
              key: "Authorization",
              value: "Basic {greenhouse_api_key_base64}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{job_title}" },
            { key: "content", value: "{external_post}" },
            { key: "external", value: "true" },
            { key: "active", value: "true" },
          ],
          variables: [
            { name: "id", customName: "external_post_id", type: "number" },
            { name: "live", customName: "external_post_live", type: "boolean" },
          ],
        },
      }),

      // 6. Social amplification scheduling
      n("6", "hitApi", "Schedule social media posts (LinkedIn)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "author", value: "urn:li:organization:{linkedin_org_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value:
                '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{linkedin_social_post} {hashtags}"},"shareMediaCategory":"NONE"}}',
            },
            {
              key: "visibility",
              value: '{"com.linkedin.ugc.MemberNetworkVisibility":"PUBLIC"}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_post_id", type: "string" },
          ],
        },
      }),

      // 7. Posting tracker (Sheets)
      sheetsNode(
        "7",
        "Log all postings to tracker (Sheets)",
        1480,
        "Channel_Postings",
        '[["{job_id}","{job_title}","{department}","{location}","{selected_channels}","{channel_count}","{external_post_id}","{linkedin_post_id}","{external_post_live}","=NOW()"]]',
      ),

      // 8. Channel failure Slack alert
      n("8", "condition", "All channels published?", 1720, {
        conditionData: [
          {
            variable: "external_post_live",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "7", "output_2"), // failure -> re-log with error flag (Slack alert inline in condition false path)
    ],
  },

  // ===================================================================
  // 727. LEAVE CALENDAR SYNC (8 nodes)
  // ===================================================================

  "Leave Calendar Sync": {
    summary:
      "Synchronizes leave/PTO events across calendars and team tools. A leave event trigger pulls details from BambooHR, creates an OOO event on the employee's Google Calendar, updates the team calendar, checks for coverage conflicts via condition, sends a conflict alert to Slack, updates the employee's Slack status, and reconciles manual edits.",
    nodes: [
      trigger("Webhook: Leave approved or updated", "webhook_app", {
        source: "BambooHR",
        event: "timeOff.approved",
        howItStarts:
          "A PTO/leave request is approved or modified in BambooHR, triggering calendar synchronization across all connected tools",
        setup:
          "Configure BambooHR webhook for timeOff.approved and timeOff.updated events. Ensure the payload includes employee ID, leave type, dates, and approval status.",
        inputFields: [],
      }),

      // 2. Leave details pull (BambooHR)
      n("2", "hitApi", "Pull leave details (BambooHR)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboo_subdomain}/v1/time_off/requests/?action=view&status=approved",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "body", customName: "leave_requests_json", type: "string" },
          ],
        },
      }),

      // 2b. AI to extract the relevant leave event
      aiNode(
        "2b",
        "AI: Extract leave event details",
        400,
        "You are an HR data processor. Parse the BambooHR leave data and webhook payload to find the specific approved leave event. Extract: employee name, email, employee ID, leave type (vacation/sick/personal/parental), start date, end date, total days, department, manager name, and any notes. Respond JSON: {employee_name: string, employee_email: string, employee_id: string, leave_type: string, start_date: string, end_date: string, total_days: number, department: string, manager_name: string, notes: string, team_calendar_id: string}",
        "BambooHR webhook: {webhook_payload}. Leave requests data: {leave_requests_json}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "leave_type", as: "leave_type", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "end_date", as: "end_date", type: "string" },
          { field: "total_days", as: "total_days", type: "number" },
          { field: "department", as: "department", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "notes", as: "leave_notes", type: "string" },
          { field: "team_calendar_id", as: "team_calendar_id", type: "string" },
        ],
      ),

      // 3. Employee calendar OOO event (Google Calendar)
      n("3", "hitApi", "Create OOO event on employee calendar", 580, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/{employee_email}/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "OOO: {employee_name} - {leave_type}" },
            { key: "start", value: '{"date":"{start_date}"}' },
            { key: "end", value: '{"date":"{end_date}"}' },
            { key: "transparency", value: "opaque" },
            {
              key: "description",
              value:
                "Leave type: {leave_type}\\nDuration: {total_days} day(s)\\nNotes: {leave_notes}",
            },
            { key: "eventType", value: "outOfOffice" },
          ],
          variables: [
            { name: "id", customName: "personal_event_id", type: "string" },
          ],
        },
      }),

      // 4. Team calendar update
      n("4", "hitApi", "Update team calendar", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/{team_calendar_id}/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "summary",
              value: "{employee_name} - {leave_type} ({total_days}d)",
            },
            { key: "start", value: '{"date":"{start_date}"}' },
            { key: "end", value: '{"date":"{end_date}"}' },
            { key: "transparency", value: "transparent" },
            {
              key: "description",
              value: "Department: {department}\\nApproved by: {manager_name}",
            },
          ],
          variables: [
            { name: "id", customName: "team_event_id", type: "string" },
          ],
        },
      }),

      // 5. Coverage conflict condition
      n("5", "condition", "Coverage conflict detected?", 1000, {
        conditionData: [
          {
            variable: "total_days",
            value: "3",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Conflict Slack alert — True branch (extended leave)
      slackNode(
        "6",
        "Alert team of extended leave (Slack)",
        1240,
        ":palm_tree: *Extended Leave Alert*\\nEmployee: {employee_name} ({department})\\nType: {leave_type}\\nDates: {start_date} to {end_date} ({total_days} days)\\nManager: {manager_name}\\n\\nPlease ensure coverage is arranged for {employee_name}'s responsibilities during this period.",
        -220,
      ),

      // 7. Slack status update
      n("7", "hitApi", "Set Slack OOO status", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/users.profile.set",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user", value: "{employee_slack_id}" },
            {
              key: "profile",
              value:
                '{"status_text":"OOO - {leave_type} until {end_date}","status_emoji":":palm_tree:","status_expiration":"{end_date_unix}"}',
            },
          ],
          variables: [
            { name: "ok", customName: "slack_status_updated", type: "boolean" },
          ],
        },
      }),

      // 8. Manual edit reconciliation
      sheetsNode(
        "8",
        "Log leave sync for reconciliation (Sheets)",
        1720,
        "Leave_Sync_Log",
        '[["{employee_id}","{employee_name}","{leave_type}","{start_date}","{end_date}","{total_days}","{personal_event_id}","{team_event_id}","{slack_status_updated}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // extended leave -> conflict alert
      e("5", "7", "output_2"), // short leave -> skip alert, set status
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 728. NEW EMPLOYEE ONBOARDING (10 nodes)
  // ===================================================================

  "New Employee Onboarding": {
    summary:
      "Comprehensive 10-step onboarding automation from hire to first-week readiness. A new hire trigger provisions email and workspace, sets up SSO and app access via Okta, sends a welcome email, orders equipment, schedules orientation, assigns a buddy, sends manager prep email, enrolls in compliance training, and tracks progress in Sheets.",
    nodes: [
      trigger("Webhook: New hire confirmed", "webhook_app", {
        source: "BambooHR / Greenhouse",
        event: "employee.hired",
        howItStarts:
          "A new hire's start date is confirmed in the HRIS or ATS, triggering the full onboarding automation pipeline",
        setup:
          "Configure BambooHR or Greenhouse to fire a webhook when a candidate is marked as hired with a confirmed start date. Include name, email, department, role, manager, start date, and location.",
        inputFields: [],
      }),

      // 2. Email/workspace provisioning
      aiNode(
        "2",
        "AI: Extract hire details & plan provisioning",
        280,
        "You are an IT onboarding specialist. Parse the new hire webhook to extract all details and determine provisioning requirements. Map department to standard tool stack (engineering: GitHub+Jira+AWS; sales: Salesforce+Gong+LinkedIn; marketing: HubSpot+Figma+Canva; all: Slack+Google Workspace+Notion). Respond JSON: {employee_name: string, employee_email: string, personal_email: string, employee_id: string, role_title: string, department: string, manager_name: string, manager_email: string, start_date: string, location: string, is_remote: string, tools_to_provision: string, workspace_type: string, equipment_needs: string}",
        "New hire payload: {webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "personal_email", as: "personal_email", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "role_title", as: "role_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "is_remote", as: "is_remote", type: "string" },
          {
            field: "tools_to_provision",
            as: "tools_to_provision",
            type: "string",
          },
          { field: "workspace_type", as: "workspace_type", type: "string" },
          { field: "equipment_needs", as: "equipment_needs", type: "string" },
        ],
      ),

      // 3. SSO/app provisioning (Okta)
      n("3", "hitApi", "Provision SSO & apps (Okta)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}/api/v1/users",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "profile",
              value:
                '{"firstName":"{employee_name}","login":"{employee_email}","email":"{employee_email}","department":"{department}","title":"{role_title}","manager":"{manager_email}"}',
            },
            { key: "groupIds", value: "[{department_group_id}]" },
            { key: "activate", value: "true" },
          ],
          variables: [
            { name: "id", customName: "okta_user_id", type: "string" },
            { name: "status", customName: "okta_status", type: "string" },
          ],
        },
      }),

      // 4. Welcome email
      emailNode(
        "4",
        "Send welcome email to new hire",
        760,
        "{personal_email}",
        "Welcome to the team, {employee_name}! Your onboarding starts {start_date}",
        "Hi {employee_name},\\n\\nWelcome! We're thrilled to have you join as {role_title} in {department}.\\n\\nYour start date: {start_date}\\nLocation: {location}\\nManager: {manager_name}\\n\\nBefore your first day:\\n1. Your work email ({employee_email}) will be active 2 days before start\\n2. You'll receive SSO setup instructions from Okta\\n3. Tools being provisioned: {tools_to_provision}\\n\\nWe can't wait to see you on Day 1!\\n\\nBest,\\nThe People Team",
      ),

      // 5. Equipment ordering
      n("5", "hitApi", "Submit equipment order", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
          headers: [
            { key: "Authorization", value: "Bearer {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"subject":"Equipment Order: {employee_name} ({role_title}) - Start {start_date}","description":"New hire equipment request:\\nEmployee: {employee_name}\\nRole: {role_title}\\nDepartment: {department}\\nLocation: {location}\\nRemote: {is_remote}\\nEquipment: {equipment_needs}\\nWorkspace: {workspace_type}\\nNeeded by: {start_date}","priority":"high","type":"task","tags":["onboarding","equipment","{department}"]}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "equipment_ticket_id",
              type: "number",
            },
          ],
        },
      }),

      // 6. Orientation meeting scheduling
      n("6", "hitApi", "Schedule orientation meeting (Google Calendar)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?sendUpdates=all",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "New Hire Orientation: {employee_name}" },
            {
              key: "start",
              value:
                '{"dateTime":"{start_date}T09:00:00","timeZone":"America/New_York"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"{start_date}T11:00:00","timeZone":"America/New_York"}',
            },
            {
              key: "attendees",
              value:
                '[{"email":"{employee_email}"},{"email":"{manager_email}"},{"email":"hr-onboarding@company.com"}]',
            },
            {
              key: "description",
              value:
                "Welcome orientation for {employee_name} ({role_title}, {department}).\\nAgenda: Company overview, team intro, IT setup, HR paperwork, office tour.",
            },
          ],
          variables: [
            { name: "id", customName: "orientation_event_id", type: "string" },
            {
              name: "htmlLink",
              customName: "orientation_link",
              type: "string",
            },
          ],
        },
      }),

      // 7. Buddy assignment
      slackNode(
        "7",
        "Assign onboarding buddy (Slack)",
        1480,
        ":wave: *Onboarding Buddy Assignment*\\n\\nA new team member, *{employee_name}*, is joining {department} as {role_title} on {start_date}.\\n\\nManager {manager_name} — please assign an onboarding buddy from the team. The buddy should:\\n- Welcome {employee_name} on Day 1\\n- Schedule daily check-ins for the first week\\n- Help navigate tools, processes, and team norms\\n\\nReply with the buddy's name to confirm.",
      ),

      // 8. Manager prep email
      emailNode(
        "8",
        "Send manager prep email",
        1720,
        "{manager_email}",
        "New Hire Prep: {employee_name} starts {start_date}",
        "Hi {manager_name},\\n\\nYour new team member {employee_name} ({role_title}) starts on {start_date}.\\n\\nOnboarding status:\\n- Email/SSO: Provisioned (Okta ID: {okta_user_id})\\n- Equipment: Ordered (Ticket #{equipment_ticket_id})\\n- Orientation: Scheduled ({orientation_link})\\n- Tools: {tools_to_provision}\\n\\nYour prep checklist:\\n1. Prepare first-week project/tasks\\n2. Confirm buddy assignment (check Slack)\\n3. Block 30 min for 1:1 on Day 1\\n4. Share team docs and context\\n\\nLet People Ops know if anything else is needed.",
      ),

      // 9. Compliance training enrollment
      n("9", "hitApi", "Enroll in compliance training", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboo_subdomain}/v1/training/record",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "employeeId", value: "{employee_id}" },
            { key: "type", value: "required_onboarding" },
            {
              key: "courses",
              value:
                '["security_awareness","anti_harassment","data_privacy","code_of_conduct"]',
            },
            { key: "dueDate", value: "{start_date_plus_14}" },
          ],
          variables: [
            {
              name: "id",
              customName: "training_enrollment_id",
              type: "string",
            },
          ],
        },
      }),

      // 10. Progress tracking (Sheets)
      sheetsNode(
        "10",
        "Log onboarding progress (Sheets)",
        2200,
        "Onboarding_Tracker",
        '[["{employee_id}","{employee_name}","{role_title}","{department}","{start_date}","{manager_name}","{okta_user_id}","{equipment_ticket_id}","{orientation_event_id}","{training_enrollment_id}","in_progress","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 729. NEW HIRE WELCOME SEQUENCE (8 nodes)
  // ===================================================================

  "New Hire Welcome Sequence": {
    summary:
      "Timed drip email sequence that nurtures new hires from offer acceptance through the first two weeks. An offer acceptance trigger generates AI role-specific welcome content, then sends a pre-boarding email (1 week out), Day-1 welcome email, Day-3 check-in, Day-7 resources email, checks engagement via condition, and sends a Day-14 feedback request.",
    nodes: [
      trigger("Webhook: Offer accepted", "webhook_app", {
        source: "Greenhouse / BambooHR",
        event: "offer.accepted",
        howItStarts:
          "A candidate accepts their offer letter, triggering the automated welcome drip sequence",
        setup:
          "Configure Greenhouse or BambooHR to fire a webhook when an offer is accepted. Include candidate name, email, role, department, start date, and hiring manager.",
        inputFields: [],
      }),

      // 2. AI role-specific welcome personalization
      aiNode(
        "2",
        "AI: Personalize welcome sequence",
        280,
        "You are an employee experience designer. Given a new hire's details, create personalized content for each stage of the welcome sequence: pre-boarding (excitement + logistics), Day 1 (warm welcome + what to expect), Day 3 (check-in + early wins), Day 7 (deeper resources + connections), and Day 14 (feedback request). Tailor tone and content to the role and department. Respond JSON: {employee_name: string, employee_email: string, role_title: string, department: string, manager_name: string, start_date: string, preboarding_content: string, day1_content: string, day3_content: string, day7_content: string, day14_content: string, team_intro: string, key_resources: string}",
        "Offer acceptance data: {webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "role_title", as: "role_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          {
            field: "preboarding_content",
            as: "preboarding_content",
            type: "string",
          },
          { field: "day1_content", as: "day1_content", type: "string" },
          { field: "day3_content", as: "day3_content", type: "string" },
          { field: "day7_content", as: "day7_content", type: "string" },
          { field: "day14_content", as: "day14_content", type: "string" },
          { field: "team_intro", as: "team_intro", type: "string" },
          { field: "key_resources", as: "key_resources", type: "string" },
        ],
      ),

      // 3. Pre-boarding email (1 week before start)
      emailNode(
        "3",
        "Pre-boarding email (1 week out)",
        520,
        "{employee_email}",
        "Getting Ready for Day 1, {employee_name}!",
        "Hi {employee_name},\\n\\nYour start date ({start_date}) is just around the corner! Here's what to expect:\\n\\n{preboarding_content}\\n\\nYour manager {manager_name} and the {department} team are excited to welcome you.\\n\\nIf you have any questions before your first day, don't hesitate to reach out.\\n\\nSee you soon!",
      ),

      // 4. Day-1 welcome email (delayed)
      n("4", "delayAction", "Wait until start date", 760, {
        delayActionDetails: {
          delayType: "date",
          delayDate: "{start_date}",
          delayTime: "08:00",
        },
      }),

      // 4b. Day-1 welcome email
      emailNode(
        "4b",
        "Day-1 welcome email",
        900,
        "{employee_email}",
        "Welcome to Day 1, {employee_name}! :tada:",
        "Hi {employee_name},\\n\\nToday is the day! Welcome to {department} as our new {role_title}.\\n\\n{day1_content}\\n\\nYour team:\\n{team_intro}\\n\\nYour manager {manager_name} has your first-week plan ready. Don't hesitate to ask questions - everyone remembers their first day!\\n\\nHere's to a great start!",
      ),

      // 5. Day-3 check-in email
      n("5", "delayAction", "Wait 3 days", 1060, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 3,
          delayUnit: "days",
        },
      }),

      // 5b. Send day-3 check-in
      emailNode(
        "5b",
        "Day-3 check-in email",
        1200,
        "{employee_email}",
        "How's It Going, {employee_name}? Day 3 Check-in",
        "Hi {employee_name},\\n\\nYou've made it through your first few days! How are things going?\\n\\n{day3_content}\\n\\nRemember, it's completely normal to feel a bit overwhelmed. Your buddy and manager {manager_name} are here to help.\\n\\nHit reply to let us know how you're doing - we read every response.",
      ),

      // 6. Day-7 resources email
      n("6", "delayAction", "Wait until day 7", 1340, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 4,
          delayUnit: "days",
        },
      }),

      // 6b. Send day-7 resources
      emailNode(
        "6b",
        "Day-7 resources email",
        1480,
        "{employee_email}",
        "One Week In! Resources for {employee_name}",
        "Hi {employee_name},\\n\\nCongratulations on completing your first week as {role_title}!\\n\\n{day7_content}\\n\\nKey resources for you:\\n{key_resources}\\n\\nYou're doing great. Keep the momentum going!",
      ),

      // 7. Engagement condition (checking email opens would be tracked upstream)
      n("7", "condition", "Employee engaged with sequence?", 1660, {
        conditionData: [
          {
            variable: "tokens_used",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Day-14 feedback request
      n("8", "delayAction", "Wait until day 14", 1840, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 7,
          delayUnit: "days",
        },
      }),

      // 8b. Send day-14 feedback
      emailNode(
        "8b",
        "Day-14 feedback request",
        1980,
        "{employee_email}",
        "Two Weeks In! Share Your Experience, {employee_name}",
        "Hi {employee_name},\\n\\nYou've been with us for two weeks now, and we'd love to hear from you.\\n\\n{day14_content}\\n\\nYour feedback helps us improve the experience for future team members. Please take 5 minutes to share your thoughts:\\n\\n[Survey Link]\\n\\nThank you for being part of the team!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4b"),
      e("4b", "5"),
      e("5", "5b"),
      e("5b", "6"),
      e("6", "6b"),
      e("6b", "7"),
      e("7", "8", "output_1"), // engaged -> continue to feedback
      e("7", "8b", "output_2"), // not engaged -> send feedback directly (skip delay)
      e("8", "8b"),
    ],
  },

  // ===================================================================
  // 730. NEW MANAGER ONBOARDING DRIP (8 nodes)
  // ===================================================================

  "New Manager Onboarding Drip": {
    summary:
      "Structured 4-week email program for new or newly promoted managers. A promotion/assignment trigger personalizes content via AI, then delivers Week-1 management fundamentals, Week-2 people leadership, Week-3 operations, Week-4 strategy emails, tracks engagement in Sheets, and schedules a 30-day HRBP check-in.",
    nodes: [
      trigger("Webhook: Manager promotion/assignment", "webhook_app", {
        source: "BambooHR / HRIS",
        event: "employee.promoted_to_manager",
        howItStarts:
          "An employee is promoted to a management role or assigned as a first-time manager, triggering the leadership development drip",
        setup:
          "Configure BambooHR to fire a webhook when an employee's role changes to include management responsibilities. Include name, email, new title, department, direct reports count, and HRBP assignment.",
        inputFields: [],
      }),

      // 2. AI program personalization
      aiNode(
        "2",
        "AI: Personalize manager development program",
        280,
        "You are a leadership development specialist. Given a new manager's profile, create a personalized 4-week learning journey. Adapt content based on whether they are a first-time manager or experienced leader in a new org. Tailor examples to their department. Respond JSON: {manager_name: string, manager_email: string, role_title: string, department: string, direct_reports: number, is_first_time_manager: string, hrbp_email: string, hrbp_name: string, week1_content: string, week2_content: string, week3_content: string, week4_content: string, program_id: string, start_date: string}",
        "Manager assignment payload: {webhook_payload}",
        [
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "role_title", as: "role_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "direct_reports", as: "direct_reports", type: "number" },
          {
            field: "is_first_time_manager",
            as: "is_first_time_manager",
            type: "string",
          },
          { field: "hrbp_email", as: "hrbp_email", type: "string" },
          { field: "hrbp_name", as: "hrbp_name", type: "string" },
          { field: "week1_content", as: "week1_content", type: "string" },
          { field: "week2_content", as: "week2_content", type: "string" },
          { field: "week3_content", as: "week3_content", type: "string" },
          { field: "week4_content", as: "week4_content", type: "string" },
          { field: "program_id", as: "program_id", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
        ],
      ),

      // 3. Week-1: Management fundamentals email
      emailNode(
        "3",
        "Week 1: Management fundamentals",
        520,
        "{manager_email}",
        "Your Leadership Journey Begins: Week 1 Fundamentals",
        "Hi {manager_name},\\n\\nCongratulations on your new role as {role_title} in {department}!\\n\\nThis is the first of four weekly guides to help you succeed as a manager of {direct_reports} team member(s).\\n\\n--- WEEK 1: MANAGEMENT FUNDAMENTALS ---\\n\\n{week1_content}\\n\\nAction items this week:\\n1. Schedule 1:1s with each direct report\\n2. Review team goals and current priorities\\n3. Set up your management toolkit\\n\\nYour HRBP {hrbp_name} ({hrbp_email}) is available for any questions.\\n\\nYou've got this!",
      ),

      // 4. Week-2: People leadership email (after 7-day delay)
      n("4", "delayAction", "Wait 1 week", 760, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 7,
          delayUnit: "days",
        },
      }),

      // 4b. Week-2 email send
      emailNode(
        "4b",
        "Week 2: People leadership",
        900,
        "{manager_email}",
        "Leadership Development: Week 2 - People Leadership",
        "Hi {manager_name},\\n\\nWeek 2 focuses on the most important part of management: your people.\\n\\n--- WEEK 2: PEOPLE LEADERSHIP ---\\n\\n{week2_content}\\n\\nThis week's focus:\\n1. Conduct meaningful 1:1 conversations\\n2. Practice active listening and coaching\\n3. Identify each team member's strengths and growth areas\\n\\nReflection: How did your first round of 1:1s go? Reply to share your experience.",
      ),

      // 5. Week-3: Operations email
      n("5", "delayAction", "Wait 1 week", 1060, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 7,
          delayUnit: "days",
        },
      }),

      // 5b. Week-3 email send
      emailNode(
        "5b",
        "Week 3: Operations & processes",
        1200,
        "{manager_email}",
        "Leadership Development: Week 3 - Operations Excellence",
        "Hi {manager_name},\\n\\nWeek 3 is about building the systems that make your team efficient.\\n\\n--- WEEK 3: OPERATIONS ---\\n\\n{week3_content}\\n\\nThis week's focus:\\n1. Review and optimize team workflows\\n2. Set clear expectations and accountability\\n3. Establish team communication norms\\n\\nYou're halfway through the program - great progress!",
      ),

      // 6. Week-4: Strategy email
      n("6", "delayAction", "Wait 1 week", 1340, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 7,
          delayUnit: "days",
        },
      }),

      // 6b. Week-4 email send
      emailNode(
        "6b",
        "Week 4: Strategic leadership",
        1480,
        "{manager_email}",
        "Leadership Development: Week 4 - Strategy & Vision",
        "Hi {manager_name},\\n\\nFinal week! Let's think about the bigger picture.\\n\\n--- WEEK 4: STRATEGIC LEADERSHIP ---\\n\\n{week4_content}\\n\\nThis week's focus:\\n1. Define your team's 90-day vision\\n2. Align team goals with company objectives\\n3. Create your personal leadership development plan\\n\\nCongratulations on completing the 4-week program! Your 30-day check-in with {hrbp_name} is being scheduled.",
      ),

      // 7. Engagement tracking (Sheets)
      sheetsNode(
        "7",
        "Log program completion (Sheets)",
        1720,
        "Manager_Development",
        '[["{program_id}","{manager_name}","{manager_email}","{role_title}","{department}","{direct_reports}","{is_first_time_manager}","{start_date}","completed","=NOW()"]]',
      ),

      // 8. 30-day HRBP check-in scheduling
      n(
        "8",
        "hitApi",
        "Schedule 30-day HRBP check-in (Google Calendar)",
        1960,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?sendUpdates=all",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "summary",
                value: "30-Day Manager Check-in: {manager_name} & {hrbp_name}",
              },
              {
                key: "start",
                value:
                  '{"dateTime":"{start_date_plus_30}T14:00:00","timeZone":"America/New_York"}',
              },
              {
                key: "end",
                value:
                  '{"dateTime":"{start_date_plus_30}T14:30:00","timeZone":"America/New_York"}',
              },
              {
                key: "attendees",
                value: '[{"email":"{manager_email}"},{"email":"{hrbp_email}"}]',
              },
              {
                key: "description",
                value:
                  "30-day check-in for {manager_name} ({role_title}).\\nProgram: New Manager Onboarding\\nDirect reports: {direct_reports}\\nTopics: Progress review, challenges, support needs, development plan.",
              },
            ],
            variables: [
              { name: "id", customName: "checkin_event_id", type: "string" },
              { name: "htmlLink", customName: "checkin_link", type: "string" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4b"),
      e("4b", "5"),
      e("5", "5b"),
      e("5b", "6"),
      e("6", "6b"),
      e("6b", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 731. ONBOARDING HELP BOT (7 nodes)
  // ===================================================================

  "Onboarding Help Bot": {
    summary:
      "AI chatbot that answers new hire onboarding questions using RAG search against onboarding docs. A chat trigger searches Pinecone for onboarding documentation, loads employee context from BambooHR, generates an AI-tailored answer, checks auto-resolve confidence via condition, creates IT/HR tickets in Zendesk for unresolved questions, and logs question patterns to Sheets.",
    nodes: [
      trigger("Chat trigger: Onboarding question", "webhook_form", {
        source: "Slack / Internal Portal",
        event: "onboarding.question",
        howItStarts:
          "A new hire asks an onboarding question via Slack bot, chat widget, or onboarding portal help button",
        setup:
          "Configure Slack bot (/onboarding-help) or portal chat widget to POST new hire questions to this workflow URL. Include employee identifier for context loading.",
        inputFields: [],
      }),

      // 2. RAG onboarding doc search (Pinecone)
      n("2", "hitApi", "RAG: Search onboarding docs (Pinecone)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/embeddings",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "text-embedding-3-small" },
            { key: "input", value: "{webhook_payload}" },
          ],
          variables: [
            {
              name: "data[0].embedding",
              customName: "question_embedding",
              type: "string",
            },
          ],
        },
      }),

      // 3. Employee context load (BambooHR)
      n("3", "hitApi", "Load employee context (BambooHR)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboo_subdomain}/v1/employees/0/?fields=firstName,lastName,workEmail,department,jobTitle,hireDate,location,supervisor",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "firstName",
              customName: "employee_first_name",
              type: "string",
            },
            {
              name: "lastName",
              customName: "employee_last_name",
              type: "string",
            },
            { name: "workEmail", customName: "employee_email", type: "string" },
            {
              name: "department",
              customName: "employee_department",
              type: "string",
            },
            { name: "jobTitle", customName: "employee_title", type: "string" },
            { name: "hireDate", customName: "hire_date", type: "string" },
            {
              name: "location",
              customName: "employee_location",
              type: "string",
            },
            {
              name: "supervisor",
              customName: "supervisor_name",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI tailored answer
      aiNode(
        "4",
        "AI: Generate tailored onboarding answer",
        760,
        "You are a helpful onboarding assistant. Given a new hire's question, their employee context (department, role, location, hire date), and relevant onboarding documentation chunks, provide a clear, accurate, and friendly answer. Tailor the response to their specific situation (e.g., remote vs. office, department-specific processes). If the question is about IT issues, benefits enrollment, or payroll, flag it for specialist routing. Respond JSON: {question: string, answer: string, confidence: number, auto_resolvable: string, requires_specialist: string, specialist_type: string, topic_category: string, sources_used: string}",
        "New hire question: {webhook_payload}. Employee: {employee_first_name} {employee_last_name}, {employee_title} in {employee_department}, {employee_location}, hired {hire_date}. Onboarding docs context: {question_embedding}",
        [
          { field: "question", as: "question", type: "string" },
          { field: "answer", as: "answer", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
          { field: "auto_resolvable", as: "auto_resolvable", type: "string" },
          {
            field: "requires_specialist",
            as: "requires_specialist",
            type: "string",
          },
          { field: "specialist_type", as: "specialist_type", type: "string" },
          { field: "topic_category", as: "topic_category", type: "string" },
          { field: "sources_used", as: "sources_used", type: "string" },
        ],
      ),

      // 5. Auto-resolve condition
      n("5", "condition", "Can auto-resolve with high confidence?", 1000, {
        conditionData: [
          {
            variable: "auto_resolvable",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. IT/HR ticket creation (Zendesk) — False branch
      n(
        "6",
        "hitApi",
        "Create support ticket (Zendesk)",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Bearer {zendesk_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"subject":"Onboarding Question: {topic_category}","description":"New hire: {employee_first_name} {employee_last_name} ({employee_email})\\nDepartment: {employee_department}\\nHire date: {hire_date}\\n\\nQuestion: {question}\\n\\nAI draft answer (low confidence): {answer}\\nConfidence: {confidence}%\\nSpecialist needed: {specialist_type}","priority":"normal","type":"question","tags":["onboarding","{topic_category}","{specialist_type}"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "support_ticket_id",
                type: "number",
              },
            ],
          },
        },
        -220,
      ),

      // 7. Question pattern logging (Sheets)
      sheetsNode(
        "7",
        "Log question pattern (Sheets)",
        1480,
        "Onboarding_Questions",
        '[["{employee_department}","{employee_location}","{topic_category}","{question}","{auto_resolvable}","{confidence}","{specialist_type}","{sources_used}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // auto-resolved -> log directly
      e("5", "6", "output_2"), // needs specialist -> create ticket
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 732. PTO REQUEST AUTOMATOR (9 nodes)
  // ===================================================================

  "PTO Request Automator": {
    summary:
      "End-to-end PTO request processing with balance checks, coverage analysis, and manager approval. A PTO request trigger checks balance and policy from BambooHR, routes invalid requests via condition, checks team calendar coverage, generates an AI coverage risk summary, sends a manager Slack approval request, processes the decision, writes to HRIS and calendar, and sends the employee notification email.",
    nodes: [
      trigger("Webhook: PTO request submitted", "webhook_app", {
        source: "BambooHR / Internal Portal",
        event: "timeOff.requested",
        howItStarts:
          "An employee submits a PTO request through BambooHR, Slack, or the internal portal, triggering the automated validation and approval flow",
        setup:
          "Configure BambooHR webhook for timeOff.requested events, or set up Slack command /pto to POST requests. Include employee ID, dates, leave type, and reason.",
        inputFields: [],
      }),

      // 2. Balance/policy check (BambooHR)
      n("2", "runCode", "Check PTO balance & policy", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse PTO request and validate against balance/policy
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data || payload;

const employeeId = data.employee_id || data.employeeId || "";
const employeeName = data.employee_name || data.name || "";
const employeeEmail = data.employee_email || data.email || "";
const department = data.department || "";
const managerEmail = data.manager_email || data.supervisor_email || "";
const managerName = data.manager_name || data.supervisor || "";
const leaveType = data.leave_type || data.type || "vacation";
const startDate = data.start_date || data.start || "";
const endDate = data.end_date || data.end || "";
const reason = data.reason || data.notes || "";

// Calculate business days
const start = new Date(startDate);
const end = new Date(endDate);
let bizDays = 0;
const d = new Date(start);
while (d <= end) {
  if (d.getDay() !== 0 && d.getDay() !== 6) bizDays++;
  d.setDate(d.getDate() + 1);
}

// Simulate balance check (in production, this calls BambooHR API)
const currentBalance = data.balance || data.available_days || 15;
const hasSufficientBalance = bizDays <= currentBalance;
const advanceNotice = Math.ceil((start - new Date()) / (1000 * 60 * 60 * 24));
const meetsNoticePolicy = advanceNotice >= 3 || leaveType === "sick";
const isValid = hasSufficientBalance && meetsNoticePolicy;

return {
  employee_id: employeeId,
  employee_name: employeeName,
  employee_email: employeeEmail,
  department: department,
  manager_email: managerEmail,
  manager_name: managerName,
  leave_type: leaveType,
  start_date: startDate,
  end_date: endDate,
  business_days: bizDays,
  reason: reason,
  current_balance: currentBalance,
  remaining_after: currentBalance - bizDays,
  is_valid_request: isValid ? "yes" : "no",
  validation_issues: !hasSufficientBalance ? "Insufficient PTO balance (" + currentBalance + " days available, " + bizDays + " requested)" : !meetsNoticePolicy ? "Advance notice policy not met (" + advanceNotice + " days, minimum 3 required)" : "No issues",
  advance_notice_days: advanceNotice
};`,
          outputVariables: [
            { name: "employee_id", type: "string" },
            { name: "employee_name", type: "string" },
            { name: "employee_email", type: "string" },
            { name: "department", type: "string" },
            { name: "manager_email", type: "string" },
            { name: "manager_name", type: "string" },
            { name: "leave_type", type: "string" },
            { name: "start_date", type: "string" },
            { name: "end_date", type: "string" },
            { name: "business_days", type: "number" },
            { name: "reason", type: "string" },
            { name: "current_balance", type: "number" },
            { name: "remaining_after", type: "number" },
            { name: "is_valid_request", type: "string" },
            { name: "validation_issues", type: "string" },
            { name: "advance_notice_days", type: "number" },
          ],
        },
      }),

      // 3. Invalid request condition
      n("3", "condition", "PTO request valid?", 520, {
        conditionData: [
          {
            variable: "is_valid_request",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Team calendar coverage check — True branch continues
      n("4", "hitApi", "Check team calendar coverage (Google Calendar)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{start_date}T00:00:00Z" },
            { key: "timeMax", value: "{end_date}T23:59:59Z" },
            { key: "items", value: "{team_calendar_emails}" },
          ],
          variables: [
            {
              name: "calendars",
              customName: "team_availability_data",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI coverage risk summary
      aiNode(
        "5",
        "AI: Assess coverage risk",
        1000,
        "You are an HR operations analyst. Given a PTO request and team availability data, assess the coverage risk for the team. Consider: how many team members are already OOO during the same period, any critical deadlines or releases, and whether the role has a backup. Rate risk as low/medium/high. Respond JSON: {team_members_ooo: number, coverage_risk: string, risk_factors: string, recommended_action: string, coverage_summary: string}",
        "Employee: {employee_name} ({department}). PTO: {start_date} to {end_date} ({business_days} days, {leave_type}). Team availability: {team_availability_data}. Reason: {reason}",
        [
          { field: "team_members_ooo", as: "team_members_ooo", type: "number" },
          { field: "coverage_risk", as: "coverage_risk", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "coverage_summary", as: "coverage_summary", type: "string" },
        ],
      ),

      // 6. Manager Slack approval
      slackNode(
        "6",
        "Request manager approval (Slack)",
        1240,
        ":calendar: *PTO Request for Approval*\\n\\nEmployee: {employee_name} ({department})\\nType: {leave_type}\\nDates: {start_date} to {end_date} ({business_days} days)\\nReason: {reason}\\n\\nBalance: {current_balance} days | After: {remaining_after} days\\nAdvance notice: {advance_notice_days} days\\n\\n*Coverage Analysis:*\\nRisk: {coverage_risk} | Team OOO: {team_members_ooo}\\n{risk_factors}\\nRecommendation: {recommended_action}\\n\\nReact :white_check_mark: to approve or :x: to deny.",
      ),

      // 7. Decision processing
      n("7", "runCode", "Process approval decision", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process the manager's approval decision
// In production, this would read the Slack reaction/response
const approved = vars.slack_sent ? "yes" : "no";
const decisionDate = new Date().toISOString().split("T")[0];
const status = approved === "yes" ? "approved" : "denied";
const statusMessage = approved === "yes"
  ? "Your PTO request has been approved! Your calendar and HRIS records will be updated."
  : "Your PTO request was not approved. Please check with your manager for details.";

return {
  approval_status: status,
  decision_date: decisionDate,
  status_message: statusMessage,
  is_approved: approved
};`,
          outputVariables: [
            { name: "approval_status", type: "string" },
            { name: "decision_date", type: "string" },
            { name: "status_message", type: "string" },
            { name: "is_approved", type: "string" },
          ],
        },
      }),

      // 8. HRIS + calendar write
      n("8", "hitApi", "Update HRIS & create calendar OOO (BambooHR)", 1720, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboo_subdomain}/v1/employees/{employee_id}/time_off/request",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "status", value: "{approval_status}" },
            { key: "start", value: "{start_date}" },
            { key: "end", value: "{end_date}" },
            { key: "timeOffTypeId", value: "{leave_type}" },
            {
              key: "notes",
              value:
                "Auto-processed. Coverage risk: {coverage_risk}. Decision: {decision_date}",
            },
          ],
          variables: [
            { name: "id", customName: "pto_request_id", type: "string" },
          ],
        },
      }),

      // 9. Employee notification email
      emailNode(
        "9",
        "Notify employee of PTO decision",
        1960,
        "{employee_email}",
        "PTO Request {approval_status}: {start_date} to {end_date}",
        "Hi {employee_name},\\n\\n{status_message}\\n\\nRequest Details:\\n- Type: {leave_type}\\n- Dates: {start_date} to {end_date} ({business_days} business days)\\n- Status: {approval_status}\\n- Decision date: {decision_date}\\n- Remaining balance: {remaining_after} days\\n\\nCoverage note: {coverage_summary}\\n\\nIf you have questions, please reach out to {manager_name} or HR.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // valid -> check coverage
      e("3", "9", "output_2"), // invalid -> notify employee with validation issues
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
